import {
  ClickAwayListener,
  Divider,
  IconButton,
  Popper,
  Stack,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronDownIcon from '@/assets/icons/ChevronDownIcon';
import { useVersioning, useFloorplanImportExport } from '@modules/floorplanV2/hooks';
import { FloorplanDesignSelector } from '@/modules/floorplanDesign/store/selectors';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { VersioningContainer } from '@/modules/versioning';
import { IsLatestRevisionSelector } from '@/selectors';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';

export const CenterMenu = () => {
  const { t } = useTranslation(['interface', 'common']);
  const { finalizeRevision, restoreVersion } = useVersioning();
  const revisionIsInDraft = useFloorplanDesignStore(FloorplanDesignSelector.revisionIsInDraft);
  const revisionId = useFloorplanDesignStore((state) => state.metaData.revisionId);
    const activeModule = useAppStore((state) => state.activeModule);
  const activeRevisionIsLatest = IsLatestRevisionSelector.useReactive();
  const anchorRef = useRef(null);
  const { downloadFloorplan } = useFloorplanImportExport();
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const [centerMenuOpen, setCenterMenuOpen] = useState(false);
  const [versioningPanelIsOpen, setVersioningPanelIsOpen] = useState(false);

  const versionHistoryDisabled = useMemo(() => 
    activeModule !== 'canvas_setup' && activeModule !== 'canvas_layout' && activeModule !== 'canvas_simulation', [activeModule]);

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCenterMenuOpen(false);
    setVersioningPanelIsOpen(false);
  }, [revisionId]);

  const saveDropDownClick = useCallback(() => {
    setCenterMenuOpen((prevOpen) => !prevOpen);
  }, []);

  const handleSaveDropDownClose = useCallback((event: Event) => {
    // @ts-expect-error strictNullChecks. Pls fix me
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setCenterMenuOpen(false);
  }, []);

  const handleDownloadFloorplan = useCallback(async () => {
    appSvc.showLoadContainer('OPAQUE');

    try {
      await downloadFloorplan();
      setCenterMenuOpen(false);
    } finally {
      appSvc.hideLoadContainer();
    }
  }, [downloadFloorplan, appSvc]);

  const save = useCallback(async () => {
    appSvc.showLoadContainer('TRANSPARENT');

    try {
      const revisionIsInDraft = FloorplanDesignSelector.revisionIsInDraft(
        useFloorplanDesignStore.getState(),
      );

      if (revisionIsInDraft) {
        await finalizeRevision();
      }
    } finally {
      appSvc.hideLoadContainer();
    }
  }, [appSvc, finalizeRevision]);

  const restore = useCallback(async () => {
    appSvc.showLoadContainer('TRANSPARENT');

    try {
      if (!activeRevisionIsLatest) {
        await restoreVersion();
      }
    } finally {
      appSvc.hideLoadContainer();
    }
  }, [appSvc, restoreVersion, activeRevisionIsLatest]);

  const onSaveClick = useCallback(() => {
    setCenterMenuOpen(false);
    save();
  }, [save]);

  const onRestoreClick = useCallback(() => {
    setCenterMenuOpen(false);
    restore();
  }, [restore]);

  const onVersionHistoryClick = useCallback(() => {
    setCenterMenuOpen(false);
    setVersioningPanelIsOpen(true);
  }, []);

  const handleVersionHistoryClose = useCallback((event: Event) => {
    // @ts-expect-error strictNullChecks. Pls fix me
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setVersioningPanelIsOpen(false);
  }, []);

  return (
    <>
      <IconButton
        aria-controls={centerMenuOpen ? 'split-button-menu' : undefined}
        aria-expanded={centerMenuOpen ? 'true' : undefined}
        aria-label='Save'
        aria-haspopup='menu'
        onClick={saveDropDownClick}
        ref={anchorRef}
      >
        <ChevronDownIcon />
      </IconButton>

      <Popper
        sx={{
          zIndex: 1,
        }}
        open={centerMenuOpen}
        // plz fix me
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement='top-end'
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'right top',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleSaveDropDownClose}>
                <Stack>
                  <MenuList id='split-button-menu' autoFocusItem>
                    {activeRevisionIsLatest ? (
                      <MenuItem onClick={onSaveClick} disabled={!revisionIsInDraft}>
                        {t('interface:versioning.save')}
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={onRestoreClick} disabled={activeRevisionIsLatest}>
                        {t('interface:versioning.restore')}
                      </MenuItem>
                    )}

                    <MenuItem onClick={handleDownloadFloorplan}>
                      {t('common:download', 'download')}
                    </MenuItem>

                    <MenuItem onClick={onVersionHistoryClick} disabled={versionHistoryDisabled}>
                      {t('interface:versioning.version_history', 'version history')}
                    </MenuItem>

                    <Divider />

                    <Typography
                      align='left'
                      fontSize={12}
                      sx={{ paddingLeft: '1.4em', paddingBottom: '0.5em' }}
                    >
                      {`${t('interface:export.export', 'export')}:`}
                    </Typography>

                    <Typography
                      align='left'
                      fontSize={10}
                      sx={{ paddingLeft: '1.4em', paddingBottom: '0.5em', paddingRight: '1.4em' }}
                    >
                      For export, please read the release notes
                    </Typography>
                  </MenuList>
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Popper
        sx={{
          zIndex: 1,
        }}
        open={versioningPanelIsOpen}
        // plz fix me
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement='top-end'
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'center top',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleVersionHistoryClose}>
                <Stack>
                  <VersioningContainer />
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
